import React from 'react';
import { useTranslate } from 'react-admin';
import { ResourceFormDialog } from '@/components/common';
import ProviderForm from './ProviderForm';
import { createEmptyProviderFormValues } from '../constants';
import { buildProviderFormData } from '../transforms/buildProviderFormData';
import type { Provider, ProviderFormValues } from '../types/provider';

export interface ProviderFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Record id when in edit mode; null = create mode. */
  recordId: string | number | null;
}

const hydrate = (record: Provider): ProviderFormValues => {
  const empty = createEmptyProviderFormValues();

  let email: ProviderFormValues['email'] = 'none';
  if (record.emailSmtpViaBasicAuthProvider) email = 'emailSmtpViaBasicAuthProvider';
  else if (record.emailSmtpViaOauthProvider) email = 'emailSmtpViaOauthProvider';
  else if (record.emailApiProvider) email = 'api';

  const twilioConfigured = Boolean(record.whatsappProvider);
  const metaConfigured = Boolean(record.whatsappMetaProvider);
  const whatsAppProviderType: ProviderFormValues['whatsAppProviderType'] =
    metaConfigured && !twilioConfigured ? 'meta' : 'twilio';

  // Each channel toggle reflects the sub-provider's active/blocked state:
  // present but isActive === false => configured yet blocked (toggle off).
  const active = (p?: { isActive?: boolean }): boolean =>
    p ? p.isActive !== false : false;
  const whatsApp = active(record.whatsappProvider ?? record.whatsappMetaProvider);

  // Team chat: presence of a row = that platform is configured (webhook is a
  // secret and never returned, so it stays blank — blank on save keeps it).
  const teamChat = { ...empty.teamChat };
  (record.teamChatProviders ?? []).forEach((t) => {
    if (teamChat[t.platform]) {
      teamChat[t.platform] = {
        enabled: t.isActive !== false,
        present: true,
        webhookUrl: '',
      };
    }
  });

  return {
    ...empty,
    id: record.id,
    providerName: record.providerName ?? '',
    providerStatus: record.providerStatus ?? 'active',
    priority: record.priority ?? 1,
    retries: record.retries ?? 1,
    email,
    sms: active(record.smsProvider),
    whatsApp,
    whatsAppProviderType,
    telegram: active(record.telegramProvider),
    viber: active(record.viberProvider),
    line: active(record.lineProvider),
    push: active(record.webAppProviderModel),
    replacePushFile: false,
    teamChat,
    emailSmtpViaBasicAuthProvider: {
      ...empty.emailSmtpViaBasicAuthProvider,
      ...record.emailSmtpViaBasicAuthProvider,
    },
    emailSmtpViaOauthProvider: {
      ...empty.emailSmtpViaOauthProvider,
      ...record.emailSmtpViaOauthProvider,
    },
    emailApiProvider: {
      ...empty.emailApiProvider,
      ...record.emailApiProvider,
    },
    smsProvider: {
      ...empty.smsProvider,
      ...record.smsProvider,
    },
    whatsappProvider: {
      ...empty.whatsappProvider,
      ...record.whatsappProvider,
    },
    whatsappMetaProvider: {
      ...empty.whatsappMetaProvider,
      ...record.whatsappMetaProvider,
    },
    telegramProvider: {
      ...empty.telegramProvider,
      ...record.telegramProvider,
    },
    viberProvider: {
      ...empty.viberProvider,
      ...record.viberProvider,
    },
    lineProvider: {
      ...empty.lineProvider,
      ...record.lineProvider,
    },
    webAppProviderModel: {
      ...empty.webAppProviderModel,
      ...record.webAppProviderModel,
    },
  };
};

export const ProviderFormDialog: React.FC<ProviderFormDialogProps> = ({
  open,
  onClose,
  recordId,
}) => {
  const translate = useTranslate();
  const isEdit = recordId !== null;
  return (
    <ResourceFormDialog
      open={open}
      onClose={onClose}
      mode={isEdit ? 'edit' : 'create'}
      resource="providers"
      recordId={recordId ?? undefined}
      title={translate('resources.providers.dialog.title')}
      createRecord={!isEdit ? createEmptyProviderFormValues() : undefined}
      createdMessage="resources.providers.notifications.created"
      updatedMessage="resources.providers.notifications.updated"
      maxWidth="xl"
      transform={(data: ProviderFormValues) => buildProviderFormData(data)}
      queryOptions={{
        select: (record: Provider) => hydrate(record) as unknown as Provider,
      }}
    >
      <ProviderForm />
    </ResourceFormDialog>
  );
};

export default ProviderFormDialog;
